/**
 * The shop checkout's behavior half (studio-shop D5, D8). Draws nothing.
 *
 * The bag is priced again here; only its available items are ordered. Once the
 * address is complete, the server quotes it: each service confirms it can make
 * and deliver its items there, and the shipping, tax and total are the server's.
 * Continue to payment places the order under a checkout key kept in this tab,
 * renewed whenever the bag, the address or the buyer changes, so a reload with
 * nothing changed returns the same unpaid order; the order's credentials stay in
 * this tab (D8). A signed-in buyer's email and address come from their last
 * paid order here, filled in once and free to change.
 *
 * shop-self-fulfilment D5: the order it made last stays beside its key, so
 * what that order sets aside is still this checkout's after a reload or a
 * changed address. When that order's payment went through after all, its page
 * opens; one still going through is said in the server's words.
 *
 * Coming back to checkout (code review High 1, Medium 2, Medium 3): the order
 * this tab made is read before anything of the bag is drawn. Paid, or back from
 * Stripe paying it, its page opens and the bag empties; still awaiting payment,
 * the checkout resumes at its card step with the details it was made with and
 * its own totals, asking no quote. Each key is kept as it is sent, so an order
 * whose answer never came is still this checkout's.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { SHOP_COUNTRIES } from '@telify/shared/shop/countries';
import { shownOptionLabel } from '@telify/shared/shop/line-label';
import type { BuyerOrderDto, CheckoutQuoteDto } from '@telify/shared/shop/site-dtos';
import { settleStripeReturn, stripeReturnPayment } from '@/lib/stripeReturn';

import { clientName, useTelegramSignIn, useWebSession } from '../../api/session';
import { SiteFetchError } from '../../api/site';
import {
  arrivalDates,
  createShopOrder,
  forgetCheckout,
  keepCheckout,
  keepOrderCredentials,
  keepSent,
  keptCheckout,
  orderClaim,
  orderReadToken,
  quoteShop,
  shopMoney,
  shopPrice,
  useBagPrice,
  useSavedDetails,
  useShopOrder,
  type NewShopOrder,
  type ShopAddress,
} from '../../api/shop';
import { usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { useBag } from '../../hooks/useBag';
import { usePageMover } from '../../pageMover';
import { contract, type ShopCheckoutActions, type ShopCheckoutEvents, type ShopCheckoutField, type ShopCheckoutState } from './contract';

export type ShopCheckoutRuntime = PieceRuntime<typeof contract, ShopCheckoutState, ShopCheckoutActions> & {
  /** Hands an expired order back: the checkout starts a new one at today's prices (D6). */
  readonly expire: () => void;
};

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS',
  'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

type Form = Record<ShopCheckoutField, string>;
const BLANK: Form = { email: '', buyerName: '', name: '', country: 'US', line1: '', line2: '', city: '', state: '', postalCode: '' };
const EMAIL = /^\S+@\S+\.\S+$/;
const QUOTE_DELAY_MS = 500;
/** Countries whose addresses need a state, as the server asks for it (D5): no quote is asked without one. */
const STATE_ASK: Readonly<Record<string, string>> = { US: 'Choose a state.', CA: 'Add your province.', AU: 'Add your state or territory.' };

/** The address as the server takes it; null while it is not complete enough to quote. */
function addressOf(form: Form): ShopAddress | null {
  const address = {
    line1: form.line1.trim(),
    line2: form.line2.trim() || null,
    city: form.city.trim(),
    state: form.state.trim() || null,
    postalCode: form.postalCode.trim(),
    country: form.country,
  };
  if (!address.line1 || !address.city || !address.postalCode || !address.country || (STATE_ASK[address.country] && !address.state)) return null;
  return address;
}

function missing(form: Form, asksBuyerName: boolean): Partial<Record<ShopCheckoutField, string>> {
  const us = form.country === 'US';
  const errors: Partial<Record<ShopCheckoutField, string>> = {};
  if (asksBuyerName && !form.buyerName.trim()) errors.buyerName = 'Enter your name.';
  if (!EMAIL.test(form.email.trim())) errors.email = 'Enter your email, so your receipt has somewhere to go.';
  if (!form.name.trim()) errors.name = 'Enter the name on the parcel.';
  if (!form.line1.trim()) errors.line1 = 'Enter a street address.';
  if (!form.city.trim()) errors.city = 'Enter a city.';
  if (STATE_ASK[form.country] && !form.state.trim()) errors.state = STATE_ASK[form.country];
  if (!form.postalCode.trim()) errors.postalCode = us ? 'Enter a ZIP code.' : 'Enter a postal code.';
  return errors;
}

/** One line of an address: "1 King St W, Unit 4, Toronto, ON M5H 1A1, Canada". */
function addressLine(address: ShopAddress): string {
  const country = address.country === 'US' ? '' : `, ${SHOP_COUNTRIES.find((c) => c.code === address.country)?.name ?? address.country}`;
  return `${address.line1}${address.line2 ? `, ${address.line2}` : ''}, ${address.city}${address.state ? `, ${address.state}` : ''} ${address.postalCode}${country}`;
}

/** D5: the checkout key for this content: the same while nothing changes, a new one otherwise, still beside the order made last and the keys sent. */
function checkoutKey(fingerprint: string, renew = false): string {
  const kept = keptCheckout();
  if (!renew && kept?.key && kept.fingerprint === fingerprint) return kept.key;
  const key = crypto.randomUUID();
  keepCheckout({ ...kept, key, fingerprint });
  return key;
}

/** Paid, in part refunded or refunded: the order's page is where its buyer goes. */
const PAID = new Set(['paid', 'partly_refunded', 'refunded']);

/**
 * shop-self-fulfilment D5: the details an order was made with, as the form holds them, and its fingerprint for the
 * bag it was made from. Read from the order itself, so a later checkout's key never puts its details on this order.
 * The buyer's own name counts only where this checkout asks for it: a signed-in buyer's is their account's
 * (shop-buyer-name), never typed here.
 */
function madeWith(order: BuyerOrderDto, asksBuyerName: boolean): { form: Form; fingerprint: string } {
  const { shipTo } = order;
  const form: Form = {
    email: order.email,
    buyerName: order.buyerName ?? '',
    name: shipTo.name,
    country: shipTo.country,
    line1: shipTo.line1,
    line2: shipTo.line2 ?? '',
    city: shipTo.city,
    state: shipTo.state ?? '',
    postalCode: shipTo.postalCode,
  };
  const lines = order.lines.map((l) => ({ optionId: l.optionId, quantity: l.quantity, ...(l.answersId ? { answers: l.answersId } : {}) }));
  return {
    form,
    fingerprint: JSON.stringify({ lines, address: addressOf(form), email: form.email, name: form.name, ...(asksBuyerName && order.buyerName ? { buyerName: order.buyerName } : {}) }),
  };
}

/** D5: the order's delivery estimate from its packages', as the quote gives it: none when any package has none. */
function arrivesOf(parts: BuyerOrderDto['parts']): CheckoutQuoteDto['arrives'] {
  if (parts.length === 0 || parts.some((p) => !p.arrivesFrom || !p.arrivesTo)) return null;
  return { from: parts.map((p) => p.arrivesFrom!).sort()[0]!, to: parts.map((p) => p.arrivesTo!).sort().at(-1)! };
}

/** The value after it has stopped changing for `ms`. */
function useSettled<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

interface PlacedOrder {
  readonly id: string;
  readonly claimToken: string;
  readonly total: string;
  readonly currency: string;
  /** What it was placed for: a change to any of it needs a new order. */
  readonly fingerprint: string;
  /** shop-self-fulfilment D5: the order as read on coming back, whose own lines and totals the summary shows. */
  readonly made?: BuyerOrderDto;
}

export function useShopCheckout(input: PieceInput<ShopCheckoutEvents> = {}): ShopCheckoutRuntime {
  const options = usePieceOptions(contract, input.options);
  const navigate = usePageMover();
  const bag = useBag();
  const priced = useBagPrice(bag.lines);
  const session = useWebSession();
  const signedIn = session.status === 'signedIn';
  const saved = useSavedDetails(signedIn);
  const telegram = useTelegramSignIn(session.status === 'signedOut');
  const openOrder = useNavigate();

  // shop-self-fulfilment D5: the order this tab made, read once on opening, before anything of the bag is drawn.
  const [earlierId] = useState(() => keptCheckout()?.orderId ?? null);
  const earlierToken = useMemo(() => (earlierId ? orderReadToken(earlierId) : null), [earlierId]);
  const earlier = useShopOrder(earlierId ?? '', earlierToken, { enabled: earlierToken !== null });
  const [returning, setReturning] = useState<'reading' | 'leaving' | null>(earlierToken ? 'reading' : null);

  const [form, setForm] = useState<Form>(BLANK);
  const [filledIn, setFilledIn] = useState(false);
  const [addressFilledIn, setAddressFilledIn] = useState(false);
  const [changing, setChanging] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [placing, setPlacing] = useState(false);
  const [order, setOrder] = useState<PlacedOrder | null>(null);
  const [expired, setExpired] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  // D5: the order route's refusal of this address (a destination closed for the beta, a postal code it cannot read), held while the address is unchanged.
  const [refusal, setRefusal] = useState<{ address: string; code: string | undefined; reason: string } | null>(null);
  // D5: each order confirms on its own; saved details and a returning checkout never tick it.
  const [adult, setAdult] = useState(false);

  // shop-self-fulfilment D5 (code review High 1, Medium 3): once the read settles, the checkout goes where its order is.
  useEffect(() => {
    if (returning !== 'reading' || earlier.isFetching) return;
    const read = earlier.data;
    if (read && (PAID.has(read.payment) || (read.payment === 'awaiting_payment' && stripeReturnPayment()))) {
      // Paid, or back from Stripe paying it: its page opens, and waits on the payment as after any payment.
      setReturning('leaving');
      forgetCheckout();
      settleStripeReturn();
      bag.clear();
      openOrder(`/shop/orders/${encodeURIComponent(read.id)}`, { replace: true, state: { thanks: true } });
      return;
    }
    setReturning(null);
    const claimToken = read?.payment === 'awaiting_payment' ? orderClaim(read.id) : null;
    if (!read || !claimToken) return;
    // Awaiting payment: its card step, with the details it was made with and its own totals.
    const made = madeWith(read, false);
    setForm(made.form);
    setOrder({ id: read.id, claimToken, total: read.total, currency: read.currency, fingerprint: made.fingerprint, made: read });
  }, [returning, earlier.isFetching, earlier.data, bag, openOrder]);

  // D8: the last paid order's email and address, once, into an address not yet typed; never over a resumed order's.
  // A bag carrying a form's answers is a gift, likely shipping to someone else: only the email fills in, and Ship to
  // starts empty (the holiday walk's order 1907, Gabriel 2026-11-02).
  const details = saved.data;
  const gift = bag.lines.some((l) => l.answers);
  useEffect(() => {
    if (!details || filledIn || returning) return;
    setFilledIn(true);
    const typed = gift || Boolean(form.line1 || form.city || form.postalCode || form.name);
    setAddressFilledIn(!typed);
    const { shipTo } = details;
    setForm((f) => ({
      ...f,
      email: f.email || details.email,
      ...(typed
        ? {}
        : {
            name: shipTo.name,
            country: shipTo.country,
            line1: shipTo.line1,
            line2: shipTo.line2 ?? '',
            city: shipTo.city,
            state: shipTo.state ?? '',
            postalCode: shipTo.postalCode,
          }),
    }));
  }, [details, filledIn, form, returning, gift]);

  const available = useMemo(() => {
    const answer = priced.data;
    if (!answer) return [];
    const quantities = new Map(bag.lines.map((l) => [l.optionId, l.quantity]));
    return answer.lines.filter((l) => l.available && quantities.has(l.optionId));
  }, [priced.data, bag.lines]);
  const lines = useMemo(() => available.map((l) => ({ optionId: l.optionId, quantity: l.quantity })), [available]);
  // site-form-buy-mode D3: a claimed line's held answers, named by id in the fingerprint (as a resumed order names them) and sent by key.
  const held = useMemo(() => new Map(bag.lines.flatMap((l) => (l.answers ? [[l.optionId, l.answers] as const] : []))), [bag.lines]);
  const withAnswers = (as: 'id' | 'key') =>
    lines.map((l) => {
      const answers = held.get(l.optionId);
      return answers ? { ...l, answers: answers[as] } : l;
    });

  const address = useMemo(() => addressOf(form), [form]);
  // Walk row 8: a guest buying with a form's answers names themselves apart from the parcel, which may be a gift.
  const asksBuyerName = session.status === 'signedOut' && held.size > 0;
  const buyer = {
    email: form.email.trim(),
    name: form.name.trim(),
    ...(asksBuyerName && form.buyerName.trim() ? { buyerName: form.buyerName.trim() } : {}),
  };
  const fingerprint = JSON.stringify({ lines: withAnswers('id'), address, ...buyer });
  // A resumed order's fingerprint is read as the checkout stands now: whether it asks the buyer's name waits on the session.
  const payable = order && (order.made ? madeWith(order.made, asksBuyerName).fingerprint : order.fingerprint) === fingerprint ? order : null;
  // shop-self-fulfilment D5 (code review Medium 3): a resumed order shows its own totals; no quote is asked for it.
  const resumed = payable?.made ?? null;

  const toQuote = useSettled(address && lines.length ? JSON.stringify({ lines, address }) : null, QUOTE_DELAY_MS);
  const quote = useQuery<CheckoutQuoteDto, SiteFetchError>({
    queryKey: ['web', 'shop', 'quote', toQuote],
    queryFn: () => {
      const body = JSON.parse(toQuote!) as { lines: { optionId: string; quantity: number }[]; address: ShopAddress };
      return quoteShop(body.lines, body.address);
    },
    enabled: toQuote !== null && !resumed,
    retry: false,
    staleTime: 10 * 60_000,
  });
  // The quote shown is for the address on screen, never an earlier one.
  const current = address && lines.length ? JSON.stringify({ lines, address }) : null;
  const orderRefusal = refusal && refusal.address === JSON.stringify(address) ? refusal : null;
  const quoted = !orderRefusal && current !== null && current === toQuote && quote.data ? quote.data : null;
  const refused = resumed
    ? null
    : (orderRefusal ?? (quote.isError && toQuote === current ? { code: quote.error.code, reason: quote.error.message } : null));
  // D5: a postal code the server cannot read is that field's error, in the server's words.
  const postalRefused = refused?.code === 'INVALID_POSTAL_CODE' ? refused.reason : null;
  const totals = resumed
    ? { subtotal: resumed.subtotal, shipping: resumed.shipping, tax: resumed.tax, total: resumed.total, arrives: arrivesOf(resumed.parts) }
    : quoted;

  const errors = { ...(attempt > 0 ? missing(form, asksBuyerName) : {}), ...(postalRefused ? { postalCode: postalRefused } : {}) };
  const currency = resumed?.currency ?? priced.data?.currency ?? 'USD';
  const money = (amount: string) => shopMoney(amount, currency);

  const place = async () => {
    if (!address || !quoted || !adult) return;
    const body: Omit<NewShopOrder, 'checkoutKey'> = { ...buyer, address, lines: withAnswers('key'), adultConfirmed: true };
    setPlacing(true);
    setProblem(null);
    // shop-self-fulfilment D5 (code review Medium 2): each key is kept as it is sent, so an order whose answer is lost is still found.
    const send = (key: string) => {
      keepSent(key);
      return createShopOrder({ ...body, checkoutKey: key });
    };
    try {
      let made;
      try {
        made = await send(checkoutKey(fingerprint));
      } catch (error) {
        // The key named an order with other content (or a paid one): a new key, once.
        if (!(error instanceof SiteFetchError && error.code === 'CHECKOUT_KEY_USED')) throw error;
        made = await send(checkoutKey(fingerprint, true));
      }
      keepOrderCredentials(made.order.id, made);
      const kept = keptCheckout();
      // The answer names the order: no key sent is still waiting on one.
      if (kept) keepCheckout({ key: kept.key, fingerprint: kept.fingerprint, orderId: made.order.id });
      setExpired(false);
      setOrder({ id: made.order.id, claimToken: made.claimToken, total: made.order.total, currency: made.order.currency, fingerprint });
    } catch (error) {
      const paid = error instanceof SiteFetchError && error.code === 'ORDER_PAID' ? error.body.orderId : undefined;
      if (typeof paid === 'string') {
        // shop-self-fulfilment D5: the checkout's earlier order was paid after all: its page opens.
        navigate(`/shop/orders/${encodeURIComponent(paid)}`, { replace: true });
      } else if (error instanceof SiteFetchError && (error.code === 'SHIPPING_UNAVAILABLE' || error.code === 'INVALID_POSTAL_CODE')) {
        setRefusal({ address: JSON.stringify(address), code: error.code, reason: error.message });
      } else {
        setProblem(error instanceof Error ? error.message : 'Something went wrong. Please try again.');
      }
    } finally {
      setPlacing(false);
    }
  };

  const actions: ShopCheckoutActions = {
    setField: (field, value) => {
      setProblem(null);
      setForm((f) => ({ ...f, [field]: value, ...(field === 'country' ? { state: '' } : {}) }));
    },
    changeAddress: () => setChanging(true),
    // One Sign in here: the test account in development (its bots are on Telegram's test server), else Telegram.
    signIn: telegram.signInOneTap,
    setAdult: (confirmed) => {
      setProblem(null);
      setAdult(confirmed);
    },
    continue: () => {
      if (Object.keys(missing(form, asksBuyerName)).length > 0 || postalRefused) {
        setAttempt((n) => n + 1);
        return;
      }
      void place();
    },
    back: () => navigate('/shop/bag'),
  };

  const expire = () => {
    setOrder(null);
    checkoutKey(fingerprint, true);
    setExpired(true);
    void priced.refetch();
    void quote.refetch();
  };

  const savedAddress =
    addressFilledIn && !changing && address && form.name.trim()
      ? { name: form.name.trim(), line: addressLine(address) }
      : null;

  const shipping: ShopCheckoutState['shipping'] = totals
    ? {
        status: 'priced',
        price: totals.shipping === '0.00' ? 'Free' : money(totals.shipping),
        // D5: the order's delivery estimate; outside the US, import taxes may be charged on arrival.
        arrives: totals.arrives ? `Arrives ${arrivalDates(totals.arrives.from, totals.arrives.to)}` : null,
        importTaxes: form.country !== 'US',
      }
    : !current || postalRefused
      ? { status: 'waiting' }
      : refused
        ? { status: 'refused', reason: refused.reason }
        : { status: 'working' };

  // The summary's items: a resumed order's own lines, else the bag's available ones.
  const summaryLines: readonly {
    optionId: string;
    productTitle: string | null;
    optionLabel: string | null;
    imageUrl: string | null;
    size: string | null;
    canvas: boolean;
    quantity: number;
    lineTotal: string | null;
  }[] = resumed?.lines ?? available;

  const client = session.user ? clientName(session.user).name : null;
  return {
    state: {
      status: returning || (!priced.data && bag.lines.length) ? 'loading' : available.length ? 'ready' : 'empty',
      session: session.status,
      client,
      canSignIn: telegram.oneTapReady,
      asksBuyerName,
      form,
      errors,
      attempt,
      countries: SHOP_COUNTRIES,
      states: US_STATES,
      savedAddress,
      filledIn: addressFilledIn,
      shipping,
      items: summaryLines.map((l) => ({
        optionId: l.optionId,
        name: l.productTitle ?? '',
        // shop-self-fulfilment D2: a label that only repeats the name is left out.
        option: shownOptionLabel(l.productTitle ?? '', l.optionLabel ?? '') ?? '',
        imageUrl: l.imageUrl,
        size: l.size,
        canvas: l.canvas,
        quantity: l.quantity,
        total: shopPrice(l.lineTotal ?? '0.00', currency),
      })),
      subtotal: priced.data ? money(totals?.subtotal ?? priced.data.subtotal) : null,
      shippingRow: totals ? (totals.shipping === '0.00' ? 'Free' : money(totals.shipping)) : 'From your address',
      tax: totals && totals.tax !== '0.00' ? money(totals.tax) : null,
      total: totals ? money(totals.total) : null,
      step: payable ? 'payment' : placing ? 'placing' : 'details',
      order: payable ? { id: payable.id, claimToken: payable.claimToken, total: payable.total, currency: payable.currency } : null,
      adultConfirmed: adult,
      expired,
      problem,
    },
    actions,
    options,
    expire,
  };
}
